import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useAppSelector } from '../store'
import { selectIsSessionExpired } from '../store/slices/sessionExpirationSlice'
import { selectIsAuthenticated } from '../store/slices/userSlice'
import { useAuthInit } from '../hooks/useAuthInit'

/**
 * Terminal-модалка при истечении сессии.
 *
 * Монтируется один раз в root layout (см. src/router/index.tsx). Рендерится
 * ТОЛЬКО при isExpired && isAuthenticated — если юзер уже на /login, модалка
 * подавляется (invariant: не показываем неавторизованному юзеру).
 *
 * Единственное действие — кнопка "Войти". Нет ESC close, нет overlay click,
 * нет dismiss. Сделано намеренно: alternate состояния (видны устаревшие
 * данные + нет способа продолжить) вреднее чем принудительный re-login.
 *
 * z-index 100010/100011 — выше InstallBanner (100000/100001) и хедера
 * DefaultLayout (99999). При одновременном появлении (редко) модалка
 * приоритетнее — бесполезно показывать "установите PWA" если сессия мертва.
 */
export function SessionExpiredModal() {
  const isExpired = useAppSelector(selectIsSessionExpired)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const navigate = useNavigate()
  const { reset } = useAuthInit()
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isExpired && isAuthenticated) {
      buttonRef.current?.focus()
    }
  }, [isExpired, isAuthenticated])

  if (!isExpired || !isAuthenticated) return null

  const handleLogin = async () => {
    try {
      await reset()
    } catch (err) {
      Sentry.captureException(err, {
        tags: { context: 'session-expired-reset' },
      })
    }
    navigate('/login', { replace: true })
  }

  return (
    <>
      <div
        className="fixed inset-0"
        style={{ zIndex: 100010, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        aria-live="assertive"
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: 100011 }}
      >
        <div
          className="rounded-lg p-6 max-w-sm w-full shadow-xl"
          style={{
            backgroundColor: 'var(--bg-base)',
            color: 'var(--text-primary)',
          }}
        >
          <h2
            id="session-expired-title"
            className="text-lg font-semibold mb-2"
          >
            Сессия истекла
          </h2>
          <p
            className="text-sm mb-4"
            style={{ color: 'var(--text-muted)' }}
          >
            Для продолжения работы войдите снова.
          </p>
          <button
            ref={buttonRef}
            type="button"
            onClick={handleLogin}
            className="w-full font-semibold"
            style={{
              backgroundColor: '#F7941D',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
            }}
          >
            Войти
          </button>
        </div>
      </div>
    </>
  )
}
